import * as React from "react"

import { cn } from "@/lib/utils"

const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement>
>(({ className, style, ...props }, ref) => (
  <div
    className={cn(
      'relative w-full overflow-x-auto my-[var(--space-4)] bg-[var(--card-bg)] border border-[var(--border)] rounded-[var(--radius-md)]',
      className
    )}
  >
    <table
      ref={ref}
      className={cn('w-full caption-bottom border-collapse text-sm bg-[var(--card-bg)]', className)}
      style={style}
      {...props}
    />
  </div>
))
Table.displayName = "Table"

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, style, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn('border-b border-[var(--border-strong)] bg-[var(--card-bg)] dark:bg-[var(--primary-medium)]', className)}
    style={style}
    {...props}
  />
))
TableHeader.displayName = "TableHeader"

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody ref={ref} className={cn(className)} {...props} />
))
TableBody.displayName = "TableBody"

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, style, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn('border-t border-[var(--border)] bg-[var(--card-bg)] font-medium', className)}
    style={style}
    {...props}
  />
))
TableFooter.displayName = "TableFooter"

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, style, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      'border-b border-[var(--border)] transition-[background-color] duration-[var(--duration-normal)] ease-[var(--ease-smooth)] hover:bg-[var(--cream-light)] dark:hover:bg-[#323235]',
      className
    )}
    style={style}
    {...props}
  />
))
TableRow.displayName = "TableRow"

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, style, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      "text-left align-middle [&:has([role=checkbox])]:pr-0",
      'h-12 px-[var(--space-4)] font-semibold text-[var(--text-primary)] bg-[var(--card-bg)] dark:bg-[var(--primary-medium)] dark:text-[var(--text-primary)]',
      className
    )}
    style={style}
    {...props}
  />
))
TableHead.displayName = "TableHead"

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, style, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      "align-middle [&:has([role=checkbox])]:pr-0",
      'p-[var(--space-4)] text-[var(--text-secondary)]',
      className
    )}
    style={style}
    {...props}
  />
))
TableCell.displayName = "TableCell"

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, style, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn('mt-[var(--space-4)] text-sm text-[var(--text-secondary)] text-left', className)}
    style={style}
    {...props}
  />
))
TableCaption.displayName = "TableCaption"

export {
  Table, TableBody, TableCaption, TableCell, TableFooter,
  TableHead, TableHeader, TableRow
}

